import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Box,
  Divider,
  Icon,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  Checkbox,
  useToast,
  useColorModeValue,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import {
  FiClock,
  FiUser,
  FiCalendar,
  FiExternalLink,
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiMoreVertical,
  FiAlertCircle,
  FiX,
} from 'react-icons/fi';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useContactDrawer } from '../../contexts/ContactDrawerContext';
import { supabase } from '../../utils/supabase';

const PRIORITY_COLORS = {
  urgent: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'gray',
};

const TYPE_LABELS = {
  crm: 'CRM',
  loan: 'Loan',
  marketing: 'Marketing',
  custom: 'Custom',
};

const STATUS_COLORS = {
  pending: 'blue',
  'in-progress': 'purple',
  completed: 'green',
  cancelled: 'gray',
};

export default function TaskDetailModal({
  isOpen,
  onClose,
  task,
  onEdit,
  onDelete,
  onComplete,
  onReopen,
  assignedUserName = null,
  createdByUserName = null,
}) {
  const toast = useToast();
  const history = useHistory();
  const contactDrawer = useContactDrawer();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBG = useColorModeValue('blue.50', 'blue.900');
  const statBG = useColorModeValue('gray.50', 'gray.700');
  const hoverBG = useColorModeValue('gray.100', 'gray.600');

  const getDueDate = () => {
    if (!task?.dueDate) return null;
    if (task.dueDate.toDate) return task.dueDate.toDate();
    if (task.dueDate instanceof Date) return task.dueDate;
    return new Date(task.dueDate);
  };

  const dueDate = getDueDate();
  const isOverdue = dueDate && moment(dueDate).isBefore(moment(), 'day') && task.status !== 'completed';
  const isCompleted = task?.status === 'completed';
  const daysUntilDue = dueDate ? moment(dueDate).diff(moment(), 'days') : null;

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      if (isCompleted) {
        await onReopen(task.id);
        toast({
          title: 'Task reopened',
          status: 'info',
          duration: 2000,
        });
      } else {
        await onComplete(task.id);
        toast({
          title: 'Task completed',
          status: 'success',
          duration: 2000,
        });
      }
      onClose();
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        title: 'Error updating task',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsCompleting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    setIsDeleting(true);
    try {
      await onDelete(task.id);
      toast({
        title: 'Task deleted',
        status: 'success',
        duration: 2000,
      });
      onClose();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast({
        title: 'Error deleting task',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleNavigateToRelated = async () => {
    if (!task.relatedTo) return;
    
    if (task.relatedTo.type === 'deal') {
      // The deal may have been deleted since the task was created — verify it
      // still exists before navigating so we never land on "deal not found".
      let dealExists = true;
      try {
        const { data, error } = await supabase
          .from('crm_deals')
          .select('id')
          .eq('id', task.relatedTo.id)
          .maybeSingle();
        if (!error) dealExists = Boolean(data);
      } catch (err) {
        // Lookup failed (e.g. offline) — fall through with the old behavior.
      }

      if (dealExists) {
        history.push(`/crm/deals/${task.relatedTo.id}`);
      } else if (task.contactId && contactDrawer?.openContactDrawer) {
        contactDrawer.openContactDrawer(task.contactId);
        toast({
          title: "This lead's deal no longer exists",
          description: 'Opened the contact instead.',
          status: 'info',
          duration: 4000,
        });
      } else {
        toast({
          title: 'This record no longer exists',
          description: 'The linked lead was deleted.',
          status: 'warning',
          duration: 4000,
        });
        return; // keep the task modal open — there is nothing to show
      }
    } else if (task.relatedTo.type === 'contact') {
      // Slide the contact drawer open on the current page; only navigate to
      // the dashboard deep-link if the app-level drawer is unavailable
      if (contactDrawer?.openContactDrawer) {
        contactDrawer.openContactDrawer(task.relatedTo.id);
      } else {
        history.push(`/crm/contacts/${task.relatedTo.id}`);
      }
    } else if (task.relatedTo.type === 'client') {
      history.push(`/in-progress/${task.relatedTo.id}`);
    }
    onClose();
  };

  if (!task) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        {/* Header */}
        <ModalHeader bg={headerBG} pb={4}>
          <HStack justify="space-between" align="flex-start">
            <VStack align="flex-start" spacing={2} flex={1}>
              <HStack spacing={3} flexWrap="wrap">
                <Checkbox
                  isChecked={isCompleted}
                  onChange={handleComplete}
                  isDisabled={isCompleting}
                  colorScheme="green"
                  size="lg"
                />
                <Text
                  fontSize="xl"
                  fontWeight="bold"
                  textDecoration={isCompleted ? 'line-through' : 'none'}
                  flex={1}
                >
                  {task.title}
                </Text>
              </HStack>
              
              <HStack spacing={2} flexWrap="wrap">
                <Badge colorScheme={STATUS_COLORS[task.status]}>
                  {task.status}
                </Badge>
                <Badge colorScheme={PRIORITY_COLORS[task.priority]}>
                  {task.priority} priority
                </Badge>
                {task.type && (
                  <Badge colorScheme="blue">
                    {TYPE_LABELS[task.type]}
                  </Badge>
                )}
                {isOverdue && (
                  <Badge colorScheme="red" display="flex" alignItems="center" gap={1}>
                    <Icon as={FiAlertCircle} boxSize={3} />
                    Overdue
                  </Badge>
                )}
              </HStack>
            </VStack>

            <HStack spacing={1} flexShrink={0}>
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<FiMoreVertical />}
                  variant="ghost"
                  size="sm"
                  aria-label="Task options"
                />
                <MenuList>
                  <MenuItem icon={<FiEdit2 />} onClick={() => {
                    onEdit(task);
                    onClose();
                  }}>
                    Edit Task
                  </MenuItem>
                  <MenuItem
                    icon={isCompleted ? <FiCheckCircle /> : <FiCheckCircle />}
                    onClick={handleComplete}
                    isDisabled={isCompleting}
                  >
                    {isCompleted ? 'Reopen Task' : 'Mark Complete'}
                  </MenuItem>
                  <Divider />
                  <MenuItem icon={<FiTrash2 />} color="red.500" onClick={handleDelete} isDisabled={isDeleting}>
                    Delete Task
                  </MenuItem>
                </MenuList>
              </Menu>
              <IconButton
                icon={<FiX />}
                variant="ghost"
                size="sm"
                aria-label="Close modal"
                onClick={onClose}
              />
            </HStack>
          </HStack>
        </ModalHeader>

        <ModalBody>
          <VStack align="stretch" spacing={6}>
            {/* Description */}
            {task.description && (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={2}>
                  Description
                </Text>
                <Text fontSize="md" whiteSpace="pre-wrap">
                  {task.description}
                </Text>
              </Box>
            )}

            <Divider />

            {/* Key Details Grid */}
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <Box p={4} bg={statBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Stat size="sm">
                    <StatLabel fontSize="xs" display="flex" alignItems="center" gap={2}>
                      <Icon as={FiCalendar} />
                      Due Date
                    </StatLabel>
                    <StatNumber fontSize="lg" color={isOverdue ? 'red.500' : 'inherit'}>
                      {dueDate ? moment(dueDate).format('MMM DD, YYYY') : 'No due date'}
                    </StatNumber>
                    {task.dueTime && (
                      <Text fontSize="sm" color="gray.600" mt={1}>
                        @ {task.dueTime}
                        {task.eventDuration && ` (${task.eventDuration} min)`}
                      </Text>
                    )}
                    {daysUntilDue !== null && !isCompleted && (
                      <Text fontSize="xs" color={isOverdue ? 'red.500' : daysUntilDue <= 1 ? 'orange.500' : 'gray.600'} mt={1}>
                        {isOverdue 
                          ? `${Math.abs(daysUntilDue)} days overdue` 
                          : daysUntilDue === 0 
                          ? 'Due today'
                          : `${daysUntilDue} days remaining`}
                      </Text>
                    )}
                  </Stat>
                </Box>
              </GridItem>

              <GridItem>
                <Box p={4} bg={statBG} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Stat size="sm">
                    <StatLabel fontSize="xs" display="flex" alignItems="center" gap={2}>
                      <Icon as={FiUser} />
                      Assigned To
                    </StatLabel>
                    <StatNumber fontSize="lg">
                      {assignedUserName || 'Unknown User'}
                    </StatNumber>
                    {createdByUserName && task.createdBy !== task.assignedTo && (
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        Created by {createdByUserName}
                      </Text>
                    )}
                  </Stat>
                </Box>
              </GridItem>

              {task.relatedTo && (
                <GridItem colSpan={2}>
                  <Box
                    p={4}
                    bg={statBG}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={borderColor}
                    cursor="pointer"
                    _hover={{ bg: hoverBG, shadow: 'sm' }}
                    transition="all 0.2s"
                    onClick={handleNavigateToRelated}
                  >
                    <HStack justify="space-between">
                      <VStack align="flex-start" spacing={1}>
                        <HStack>
                          <Icon as={FiExternalLink} color="purple.500" />
                          <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                            Related {task.relatedTo.type === 'deal' ? 'Lead' : task.relatedTo.type}
                          </Text>
                        </HStack>
                        <Text fontSize="lg" fontWeight="bold" color="purple.600">
                          {task.relatedTo.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Click to open
                        </Text>
                      </VStack>
                      <Icon as={FiExternalLink} boxSize={5} color="purple.500" />
                    </HStack>
                  </Box>
                </GridItem>
              )}
            </Grid>

            {/* Timeline */}
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={3}>
                Timeline
              </Text>
              <VStack align="stretch" spacing={2}>
                {task.createdAt && (
                  <HStack fontSize="sm">
                    <Icon as={FiClock} color="gray.500" />
                    <Text color="gray.600">Created:</Text>
                    <Text fontWeight="medium">
                      {moment(task.createdAt.toDate ? task.createdAt.toDate() : task.createdAt).format('MMM DD, YYYY @ h:mm A')}
                    </Text>
                  </HStack>
                )}
                
                {task.updatedAt && (
                  <HStack fontSize="sm">
                    <Icon as={FiClock} color="gray.500" />
                    <Text color="gray.600">Last Updated:</Text>
                    <Text fontWeight="medium">
                      {moment(task.updatedAt.toDate ? task.updatedAt.toDate() : task.updatedAt).format('MMM DD, YYYY @ h:mm A')}
                    </Text>
                  </HStack>
                )}

                {task.completedAt && (
                  <HStack fontSize="sm">
                    <Icon as={FiCheckCircle} color="green.500" />
                    <Text color="gray.600">Completed:</Text>
                    <Text fontWeight="medium" color="green.600">
                      {moment(task.completedAt.toDate ? task.completedAt.toDate() : task.completedAt).format('MMM DD, YYYY @ h:mm A')}
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Box>

            {/* Calendar Event Info */}
            {task.isCalendarEvent && (
              <Box p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                <HStack>
                  <Icon as={FiCalendar} color="blue.500" />
                  <Text fontSize="sm" fontWeight="medium" color="blue.700">
                    This is a calendar event
                    {task.eventDuration && ` (${task.eventDuration} minutes)`}
                  </Text>
                </HStack>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={borderColor}>
          <HStack spacing={3} width="full" justify="space-between">
            <Button
              leftIcon={<FiTrash2 />}
              colorScheme="red"
              variant="ghost"
              onClick={handleDelete}
              isLoading={isDeleting}
              loadingText="Deleting..."
            >
              Delete
            </Button>
            
            <HStack>
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
              
              {!isCompleted ? (
                <>
                  <Button
                    colorScheme="green"
                    leftIcon={<FiCheckCircle />}
                    onClick={handleComplete}
                    isLoading={isCompleting}
                  >
                    Mark Complete
                  </Button>
                  <Button
                    colorScheme="blue"
                    leftIcon={<FiEdit2 />}
                    onClick={() => {
                      onEdit(task);
                      onClose();
                    }}
                  >
                    Edit Task
                  </Button>
                </>
              ) : (
                <Button
                  colorScheme="blue"
                  leftIcon={<FiCheckCircle />}
                  onClick={handleComplete}
                  isLoading={isCompleting}
                >
                  Reopen Task
                </Button>
              )}
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

